import React from 'react';
import Button from '../../components/Form/Button';
import { OffCrewType } from '../../types';
import {
  cycleStatus, countWorkedDays,
  OffCrewRow, offCrewTypeLabels, offCrewNamePlaceholders, tableInputCls,
} from './timesheetUtils';
import DayCells from './DayCells';

interface OffCrewSectionProps {
  month: string; // yyyy-MM
  rows: OffCrewRow[];
  onChange: (key: string, row: OffCrewRow) => void;
  onAdd: (type: OffCrewType) => void;
  onRemove: (key: string) => void;
}

const typeBadgeCls: Record<OffCrewType, string> = {
  office: 'bg-blue-100 text-blue-800',
  visitor: 'bg-purple-100 text-purple-800',
  manpower: 'bg-orange-100 text-orange-800',
};

/** "Off Crew Personnel" rows (Office / Visitor / Manpower) in the same
 *  stacked layout as the crew grid: name row on top, day cells below. */
const OffCrewSection: React.FC<OffCrewSectionProps> = ({ month, rows, onChange, onAdd, onRemove }) => {
  const toggleDay = (row: OffCrewRow, day: number) => {
    const next = cycleStatus(row.days[String(day)]);
    onChange(row.key, { ...row, days: { ...row.days, [String(day)]: next } });
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Off Crew Personnel</h3>
          <p className="text-xs text-gray-500">
            Office: staff name + period on board · Visitor: name/count + organization (e.g. عدد 4 افراد ورشة ...) · Manpower: labor for specific works
          </p>
        </div>
        <div className="flex gap-2">
          {(['office', 'visitor', 'manpower'] as OffCrewType[]).map((type) => (
            <Button key={type} type="button" variant="secondary" size="sm" onClick={() => onAdd(type)}>
              + {offCrewTypeLabels[type]}
            </Button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6">No off-crew personnel for this month</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {rows.map((row) => {
            const worked = countWorkedDays(row.days);
            return (
              <div key={row.key} className="px-5 py-4 hover:bg-gray-50/60">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${typeBadgeCls[row.type]}`}>
                    {offCrewTypeLabels[row.type]}
                  </span>
                  <div className="w-full sm:w-80">
                    <input
                      type="text"
                      value={row.name}
                      onChange={(e) => onChange(row.key, { ...row, name: e.target.value })}
                      placeholder={offCrewNamePlaceholders[row.type]}
                      className={tableInputCls}
                    />
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${worked > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500'}`}>
                    {worked} {worked === 1 ? 'day' : 'days'}
                  </span>
                  <div className="flex-1 min-w-[180px] max-w-md ml-auto">
                    <input
                      type="text"
                      value={row.description}
                      onChange={(e) => onChange(row.key, { ...row, description: e.target.value })}
                      placeholder="Notes..."
                      className={tableInputCls}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemove(row.key)}
                    title="Remove row"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full w-8 h-8 font-bold text-lg leading-none"
                  >
                    ×
                  </button>
                </div>
                <DayCells month={month} days={row.days} onToggle={(day) => toggleDay(row, day)} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OffCrewSection;
